<template>
  <div>
    <v-combobox
      ref="combobox"
      :disabled="isDisabled"
      :label="i18n.global.t('app.material.info.name')"
      :items="filteredMaterialList"
      :item-props="(item) => item.material"
      :model-value="
        isEditing
          ? materialInputText || null
          : `${materialInputText} ${
              materialStore.selectedMatNrDescription.de ? '(' + materialStore.selectedMatNrDescription.de + ')' : ''
            }`
      "
      @update:model-value="
        (val: string|null) => {
          isEditing = true;
          materialInputText = val ? val : '';
        }
      "
      @change="checkMaterialInput"
      @update:focused="
        (val) => {
          isEditing = val;
        }
      "
      v-on:keyup.enter="checkMaterialInput"
      :error-messages="
        supplierStore.selectedSupplier && !materialInputText
          ? i18n.global.t('app.material.tooltip.input')
          : materialStore.loadingState === 'failed'
          ? i18n.global.t('app.material.loadingData.invalidShort')
          : undefined
      "
    >
      <template #item="{ props }">
        <div class="grid grid-cols-[1fr_auto] items-center">
          <v-list-item
            class="w-full"
            :title="String((props?.value as any)?.material)"
            :subtitle="String((props?.value as any)?.text)"
            @click="
              async () => {
                materialInputText = String((props?.value as any)?.material);
                await nextTick();
                combobox ? (combobox.menu = false) : '';
                isEditing = false;
                blurFunction ? blurFunction() : '';
                checkMaterialInput();
              }
            "
          >
          </v-list-item>
          <v-btn
            @click="removeSavedMaterial((props?.value as any).material)"
            icon="mdi-delete"
            size="small"
            variant="text"
          ></v-btn>
        </div>
      </template>
      <template #append>
        <div
          @mousedown="
            async () => {
              await nextTick();
              isEditing = false;
              blurFunction ? blurFunction() : '';
            }
          "
        >
          <v-btn
            @click="
              () => {
                isEditing = false;
                blurFunction ? blurFunction() : '';
                checkMaterialInput();
              }
            "
            :color="!supplierStore.selectedSupplier ? '' : 'primary'"
          >
            <div class="flex justify-center items-center mr-2">
              <v-fade-transition leave-absolute>
                <v-progress-circular
                  v-if="materialStore.loadingState === 'loading'"
                  width="2"
                  :size="22"
                  indeterminate
                ></v-progress-circular>
                <v-icon v-else-if="materialStore.loadingState === 'success' && showMaterialCheckSuccessful" :size="22"
                  >mdi-check-circle-outline</v-icon
                >
                <v-icon :size="22" v-else>mdi-magnify</v-icon>
              </v-fade-transition>
            </div>
            {{ i18n.global.t("global.search") }}</v-btn
          >
        </div>
      </template>
    </v-combobox>
    <v-snackbar v-model="showChangedMaterialSnackbar" :timeout="5000">
      {{ i18n.global.t("app.material.info.removeCharacters") }}
      <template v-slot:actions>
        <v-btn color="primary" variant="text" @click="showChangedMaterialSnackbar = false">{{
          i18n.global.t("global.close")
        }}</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
// modules
import { ref, watch, nextTick, computed } from "vue";
// helpers
import { i18n } from "@/locales/index";
// instances
import { useMaterialStore, useSupplierStore } from "@/store";

// get store instances
const materialStore = useMaterialStore();
const supplierStore = useSupplierStore();

const blurFunction = ref<() => void | undefined>();
const focusFunction = ref<() => void | undefined>();

// component data
const allMaterialList = ref<Map<string, Map<string, string>>>(new Map());
const filteredMaterialList = ref<{ material: string; text: string }[]>([]);
const inputElement = ref<HTMLInputElement>();
const materialInputText = ref("");

// show small alert, when user inputs a material with special characters that were removed
const showChangedMaterialSnackbar = ref(false);

// VCombobox ref=combobox is a vuetify object, but i cant find the correct type for it
const combobox = ref<{ menu: boolean } | null>(null);

// prevent user input
const isDisabled = computed(() => {
  if (!supplierStore.selectedSupplier) return true;
  if (materialStore.loadingState === "loading") return true;
  return false;
});

const isEditing = ref(false);

const showMaterialCheckSuccessful = ref(false);
watch(
  () => materialStore.loadingState,
  (newState) => {
    if (newState === "success") {
      showMaterialCheckSuccessful.value = true;
      setTimeout(() => {
        showMaterialCheckSuccessful.value = false;
      }, 2000);
    }
  }
);

watch(
  () => materialStore.selectedMatNrDescription.de,
  async (val) => {
    // update material text in storage
    if (
      val &&
      materialStore.loadingState === "success" &&
      supplierStore.selectedSupplier &&
      materialStore.selectedMatNr
    ) {
      const storedMaterials = allMaterialList.value.get(supplierStore.selectedSupplier.SupplierId);

      if (storedMaterials) {
        storedMaterials.set(materialStore.selectedMatNr, val);
      }

      // save to storage
      localStorage.setItem(
        "valid-materials",
        JSON.stringify(Array.from(allMaterialList.value.entries()).map((e) => [e[0], Array.from(e[1].entries())]))
      );

      getLocalMaterials();
    }

    if (!isEditing.value) {
      await nextTick();
      inputElement.value?.blur();
      if (blurFunction.value) blurFunction.value();
      if (combobox.value) combobox.value.menu = false;
    }
  }
);

// focus on input element after supplier selection
watch(
  () => supplierStore.selectedSupplier,
  async (selectedSupplier, oldSelectedSupplier) => {
    await nextTick();
    inputElement.value?.focus();
    isEditing.value = true;
    if (focusFunction.value) focusFunction.value();

    // if change then clear input
    if (oldSelectedSupplier) {
      materialInputText.value = "";
    }

    if (!selectedSupplier) return;
    // get last selected materials
    getFilteredMaterials();
  }
);

async function checkMaterialInput() {
  if (!supplierStore.selectedSupplier) return;
  if (!materialInputText.value) {
    materialStore.selectedMatNr = "";
    materialStore.loadingState = "initial";
    return;
  }

  (async () => {
    await nextTick();
    isEditing.value = false;
    blurFunction.value ? blurFunction.value() : "";
  })();

  if (combobox.value) combobox.value.menu = false;

  let isValid = false;
  try {
    await materialStore.checkMaterial(materialInputText.value);
    isValid = true;
  } catch (error) {
    if (error instanceof Error && error?.name == "AbortError") return;
    //
  }

  // when first check fails and the input includes non-numeric characters,
  // remove them and try again (e.g. when user inputs a material with dots or spaces "9681.808")
  if (!isValid) {
    const regexNonNumerics = /\D/g;
    // check without any non-numeric characters
    if (regexNonNumerics.test(materialInputText.value)) {
      const newString = String(materialInputText.value).replace(/\D/g, "");
      try {
        await materialStore.checkMaterial(newString);
        isValid = true;
        materialInputText.value = newString;

        // alert user of change
        showChangedMaterialSnackbar.value = true;
      } catch (error) {
        if (error instanceof Error && error?.name == "AbortError") return;
        //
      }
    }
  }

  // focus again on input element
  if (!isValid) {
    inputElement.value?.focus();
    if (focusFunction.value) focusFunction.value();
    return;
  }

  // add to list to save in storage
  const foundInFilter = !!filteredMaterialList.value.find((e) => e.material === materialInputText.value);
  if (!foundInFilter)
    filteredMaterialList.value.push({
      material: materialInputText.value,
      text: "",
    });

  //remove duplicated
  const uniqueMaterials: { material: string; text: string }[] = [];
  for (const iterator of filteredMaterialList.value) {
    if (!uniqueMaterials.find((e) => e.material === iterator.material)) uniqueMaterials.push(iterator);
  }

  allMaterialList.value.set(
    supplierStore.selectedSupplier.SupplierId,
    new Map(uniqueMaterials.map((e) => [e.material, e.text]))
  );

  // save to storage
  localStorage.setItem(
    "valid-materials",
    JSON.stringify(Array.from(allMaterialList.value.entries()).map((e) => [e[0], Array.from(e[1].entries())]))
  );

  // un-focus on input element
  isEditing.value = false;
  await nextTick();
  inputElement.value?.blur();
  if (blurFunction.value) blurFunction.value();
  if (combobox.value) combobox.value.menu = false;

  const foundKey = allMaterialList.value.get(supplierStore.selectedSupplier.SupplierId)?.get(materialInputText.value);
  if (foundKey) {
    materialStore.selectedMatNrDescription.de = foundKey;
  }
}

// parse selectable materials from locale storage
getLocalMaterials();
function getLocalMaterials() {
  allMaterialList.value.clear();

  try {
    const parsedData = new Map<string, [string, string][]>(JSON.parse(localStorage.getItem("valid-materials") ?? ""));
    for (const [key, value] of parsedData.entries()) {
      if (!Array.isArray(value)) continue;

      allMaterialList.value.set(String(key), new Map(value));
    }
  } catch (error) {
    // no need for error handling
    // because even if this fails, it's not necessary for the usage of the app
  }
}
function getFilteredMaterials() {
  filteredMaterialList.value = [];
  if (!supplierStore.selectedSupplier) return;

  const foundSupplier = allMaterialList.value.get(supplierStore.selectedSupplier.SupplierId);

  if (!foundSupplier) return;

  filteredMaterialList.value = Array.from(foundSupplier.entries()).map((e) => ({
    material: e[0],
    text: String(e[1]),
  }));
}

function removeSavedMaterial(material: string) {
  if (!supplierStore.selectedSupplier) return;

  // remove entry
  filteredMaterialList.value = filteredMaterialList.value.filter((e) => e.material != material);

  // set to all materials list
  allMaterialList.value.set(
    supplierStore.selectedSupplier.SupplierId,
    new Map(filteredMaterialList.value.map((e) => [e.material, e.text]))
  );

  // save to storage
  localStorage.setItem(
    "valid-materials",
    JSON.stringify(Array.from(allMaterialList.value.entries()).map((e) => [e[0], Array.from(e[1].entries())]))
  );
}
</script>
<style scoped lang="scss"></style>
